import { useState, type ReactNode } from 'react'
import {
  IconeAlerta,
  IconeChevronDireita,
  IconeChevronEsquerda,
  IconeProcedimentos,
  IconeFechar,
  IconeInfo,
  IconeSelecao,
  IconeFerramenta,
  IconeVitima,
} from '../icones'
import type { ConteudoInspetor } from '../modulos/tipos'
import type { ModoLayout } from './useModoLayout'

export type AbaInspetor = 'operacao' | 'propriedades' | 'checklist'

interface Props {
  conteudoPropriedades?: ConteudoInspetor | null
  conteudoOperacao?: ReactNode
  conteudoChecklist?: ReactNode
  guiaRapido?: ReactNode
  tituloModulo: string
  modoLayout: ModoLayout
  recolhido: boolean
  aoAlternarRecolhido: () => void
  abaAtiva?: AbaInspetor
  aoTrocarAba?: (aba: AbaInspetor) => void
  aoLimparSelecao?: () => void
  /** Mostra só a área de Operação, usada pelo roteiro clínico do tabletop. */
  somenteOperacao?: boolean
  /** Mostra só Propriedades no módulo Estrutura Veicular. */
  somentePropriedades?: boolean
}

/**
 * Painel Inspetor Técnico estilo CAD / Blender (com abas técnicas e sanfonas).
 *
 * Permite alternar entre os Parâmetros da Operação, as Propriedades do elemento
 * selecionado e o Checklist de Riscos, além de poder ser recolhido lateralmente
 * para dar 100% de largura ao Viewport 3D.
 */
export function InspetorCAD({
  conteudoPropriedades,
  conteudoOperacao,
  conteudoChecklist,
  guiaRapido,
  tituloModulo,
  modoLayout,
  recolhido,
  aoAlternarRecolhido,
  abaAtiva: abaProp,
  aoTrocarAba: aoTrocarAbaProp,
  aoLimparSelecao,
  somenteOperacao = false,
  somentePropriedades = false,
}: Props) {
  const [abaInterna, setAbaInterna] = useState<AbaInspetor>('operacao')
  const abaAtiva = abaProp ?? abaInterna
  const setAba = aoTrocarAbaProp ?? setAbaInterna

  const temSelecao = conteudoPropriedades != null
  const flutuante = modoLayout === 'tablet'

  // Quando recolhido no desktop/tablet, vira uma barra vertical esguia com os ícones das abas
  if (recolhido && modoLayout !== 'mobile') {
    return (
      <aside className="inspetor-cad inspetor-cad--recolhido" aria-label="Inspetor recolhido">
        <button
          type="button"
          className="inspetor-cad__botao-expansor"
          onClick={aoAlternarRecolhido}
          title="Expandir Inspetor [Ctrl+B]"
          aria-label="Expandir Inspetor"
        >
          <IconeChevronEsquerda tamanho={16} />
        </button>

        {!somenteOperacao && <div className="inspetor-cad__abas-recolhidas">
          {!somentePropriedades && <button
            type="button"
            className={`inspetor-cad__aba-mini${abaAtiva === 'operacao' ? ' ativo' : ''}`}
            onClick={() => {
              setAba('operacao')
              aoAlternarRecolhido()
            }}
            title="Operação e Parâmetros"
          >
            <IconeFerramenta tamanho={16} />
          </button>}
          <button
            type="button"
            className={`inspetor-cad__aba-mini${abaAtiva === 'propriedades' ? ' ativo' : ''}${temSelecao ? ' tem-dado' : ''}`}
            onClick={() => {
              setAba('propriedades')
              aoAlternarRecolhido()
            }}
            title={temSelecao ? `Propriedades: ${conteudoPropriedades?.titulo}` : 'Propriedades da Seleção'}
          >
            <IconeSelecao tamanho={16} />
          </button>
          {!somentePropriedades && <button
            type="button"
            className={`inspetor-cad__aba-mini${abaAtiva === 'checklist' ? ' ativo' : ''}`}
            onClick={() => {
              setAba('checklist')
              aoAlternarRecolhido()
            }}
            title="Riscos e Checklist"
          >
            <IconeProcedimentos tamanho={16} />
          </button>}
        </div>}
      </aside>
    )
  }

  return (
    <aside
      className={`inspetor-cad${flutuante ? ' inspetor-cad--flutuante' : ''}`}
      aria-label={`Inspetor do módulo ${tituloModulo}`}
    >
      {/* Cabeçalho do Inspetor CAD */}
      <header className="inspetor-cad__topo">
        <div className="inspetor-cad__identidade">
          <span className="inspetor-cad__tag-cad">CAD INSPECTOR</span>
          <strong className="inspetor-cad__titulo-modulo">{tituloModulo}</strong>
        </div>

        <button
          type="button"
          className="inspetor-cad__botao-colapsar"
          onClick={aoAlternarRecolhido}
          title="Recolher painel lateral [Ctrl+B]"
          aria-label="Recolher painel lateral"
        >
          <IconeChevronDireita tamanho={16} />
        </button>
      </header>

      {/* Barra de Abas Técnicas */}
      {!somenteOperacao && <nav className="inspetor-cad__navegacao-abas" role="tablist">
        {!somentePropriedades && <button
          type="button"
          role="tab"
          aria-selected={abaAtiva === 'operacao'}
          className={`inspetor-cad__aba${abaAtiva === 'operacao' ? ' ativo' : ''}`}
          onClick={() => setAba('operacao')}
        >
          <IconeFerramenta tamanho={14} />
          <span>Operação</span>
        </button>}

        <button
          type="button"
          role="tab"
          aria-selected={abaAtiva === 'propriedades'}
          className={`inspetor-cad__aba${abaAtiva === 'propriedades' ? ' ativo' : ''}${temSelecao ? ' tem-selecao' : ''}`}
          onClick={() => setAba('propriedades')}
        >
          <IconeSelecao tamanho={14} />
          <span>Propriedades</span>
          {temSelecao && <span className="inspetor-cad__badge-dot" />}
        </button>

        {!somentePropriedades && <button
          type="button"
          role="tab"
          aria-selected={abaAtiva === 'checklist'}
          className={`inspetor-cad__aba${abaAtiva === 'checklist' ? ' ativo' : ''}`}
          onClick={() => setAba('checklist')}
        >
          <IconeProcedimentos tamanho={14} />
          <span>Checklist</span>
        </button>}
      </nav>}

      {/* Conteúdo da Aba Selecionada */}
      <div className="inspetor-cad__corpo">
        {!somentePropriedades && (somenteOperacao || abaAtiva === 'operacao') && (
          <div className="inspetor-cad__secao-operacao">
            {guiaRapido && (
              <div className="inspetor-cad__guia-rapido">
                {guiaRapido}
              </div>
            )}
            {conteudoOperacao}
          </div>
        )}

        {(somentePropriedades || abaAtiva === 'propriedades') && (
          <div className="inspetor-cad__secao-propriedades">
            {temSelecao ? (
              <div className="inspetor-cad__cartao-selecao">
                <div className="inspetor-cad__subtopo">
                  <div className="inspetor-cad__info-entidade">
                    <span className="inspetor-cad__etiqueta-alvo">Elemento Ativo</span>
                    <h3 className="inspetor-cad__nome-alvo">{conteudoPropriedades.titulo}</h3>
                    {conteudoPropriedades.subtitulo && (
                      <span className="inspetor-cad__subtitulo-alvo">{conteudoPropriedades.subtitulo}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="inspetor-cad__btn-limpar"
                    onClick={conteudoPropriedades.aoFechar ?? aoLimparSelecao}
                    title="Desmarcar seleção"
                    aria-label="Desmarcar seleção"
                  >
                    <IconeFechar tamanho={14} />
                  </button>
                </div>

                {conteudoPropriedades.propriedades && conteudoPropriedades.propriedades.length > 0 && (
                  <dl className="inspetor-cad__lista-propriedades">
                    {conteudoPropriedades.propriedades.map((propriedade) => (
                      <div key={propriedade.rotulo} className="inspetor-cad__linha-propriedade">
                        <dt>{propriedade.rotulo}</dt>
                        <dd>{propriedade.valor}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {conteudoPropriedades.acoes && (
                  <div className="inspetor-cad__acoes-entidade">
                    {conteudoPropriedades.acoes}
                  </div>
                )}
              </div>
            ) : (
              <div className="inspetor-cad__estado-vazio">
                <div className="inspetor-cad__icone-vazio">
                  <IconeVitima tamanho={28} />
                </div>
                <h4>Nenhum elemento selecionado</h4>
                <p>
                  Clique na vítima, em uma porta ou em componentes da estrutura no 3D
                  para visualizar especificações, biomecânica e manobras.
                </p>
                <div className="inspetor-cad__dicas-teclado">
                  <span className="inspetor-cad__dica-item"><kbd>F</kbd> Enquadrar foco</span>
                  <span className="inspetor-cad__dica-item"><kbd>1-4</kbd> Vistas 3D</span>
                  <span className="inspetor-cad__dica-item"><kbd>Ctrl+B</kbd> Recolher</span>
                </div>
              </div>
            )}
          </div>
        )}

        {!somentePropriedades && abaAtiva === 'checklist' && (
          <div className="inspetor-cad__secao-checklist">
            {conteudoChecklist ?? (
              <div className="inspetor-cad__estado-vazio">
                <div className="inspetor-cad__icone-vazio">
                  <IconeProcedimentos tamanho={28} />
                </div>
                <h4>Procedimentos & Riscos</h4>
                <p>Nenhuma pendência ou risco crítico registrado no momento.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}
